import {
  BackdropCircularProgress,
  EnhancedSnackbar,
  ITest,
  LabelPosition,
  RHookFormTextFieldWithLabel,
  SingleColumnLayout,
  StandardPanel,
  StandardSubmitFormButtonsToolbar,
} from "@websential/cosmic";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { INITIAL_SNACKBAR_DATA, LOOKUP_FIELDS_SCREEN_SLUG, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { Grid } from "@mui/material";
import { IAppState } from "../../../store/reducers";
import { ILookupFieldSetupRequestPayload } from "../../../models/lookupField/ILookupFieldSetupRequestPayload";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { LookupFieldsSetupValidationSchema } from "./validator";
import { instanceOfILookupFieldResponce } from "../../../common/instance-method";
import { setSelectedActivityLogActions } from "../../../store/actions/activityLogActions";
import { setSelectedStickyNavBar } from "../../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface ILookupFieldSetup extends ITest { }

// for edit
const LOOKUP_FIELDS_DATA_QUERY_KEY = "get-lookup-field-data";

const useGetData = (id: string | undefined) =>
  useQuery({
    queryKey: [LOOKUP_FIELDS_DATA_QUERY_KEY],
    queryFn: () => { return LookupFieldService.getById(id) },
    refetchOnWindowFocus: false
  });


export const LookupFieldSetup: FC<ILookupFieldSetup> = ({ testId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
  // const [isInitialize, setisInitialize] = useState(false);
  const createdBy = useSelector((state: IAppState) => state.user.profile.email);

  // for edit
  const queryClient = useQueryClient();
  const { id } = useParams();


  const DEFAULT_VALUES = {
    // name: "",
    // createdBy:createdBy
  };

  const { isPending: isSubmitting, mutate: submitFormData } = useMutation({
    mutationFn: LookupFieldService.create,

    onSuccess: (
      res: ILookupFieldSetupRequestPayload | IServerError | IResponse | any
    ) => {
      if (instanceOfILookupFieldResponce(res)) {
        queryClient.invalidateQueries({ queryKey: [LOOKUP_FIELDS_DATA_QUERY_KEY, res.id] });
        setSnackbarData({
          open: true,
          message: "Lookup Field data has been submitted successfully!",
          severity: "success",
        });
      }
      let path = "/lookup-fields";
      navigate(path);
    },
    onError: (err) => {
      // TODO: customize the toast message based on the response
      setSnackbarData({
        open: true,
        message: "Something went wrong, try again later!",
        severity: "error",
      });
    },
  }
  );

  // for edit
  const { isPending: isLoadingData, data: selectedLookupFieldData } =
    useGetData(id);

  const onSnackbarClosed = (
    _event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarData(INITIAL_SNACKBAR_DATA);
  };

  const onSubmit = (formData: ILookupFieldSetupRequestPayload) => {
    submitFormData(formData);
  };

  const onResetClicked = () => {
    setSnackbarData({
      open: true,
      message: "Form was reset successfully",
      severity: "success",
    });
    formMethods.reset(DEFAULT_VALUES);
  };

  const formMethods = useForm<any>({
    resolver: yupResolver(LookupFieldsSetupValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_VALUES,
  });

  // for edit
  useEffect(() => {
    // if the selected flex field data has loaded.
    if (selectedLookupFieldData) {
      Object.keys(selectedLookupFieldData).forEach((fieldName) => {

        // @ts-ignore
        const fieldValue = selectedLookupFieldData[fieldName];

        // @ts-ignore
        formMethods.setValue(fieldName, fieldValue ?? undefined);
      });
    }
  }, [selectedLookupFieldData, formMethods.setValue, formMethods]);

  useEffect(() => {
    dispatch(
      setSelectedActivityLogActions({
        entityId1: "",
        entityId2: "",
        entityId3: "",
        screenName: LOOKUP_FIELDS_SCREEN_SLUG,
        companyName: '',
        activityType:
          "activity_type=create&activity_type=updated",
        activityLogVisibility: true,
      })
    );
    dispatch(setSelectedStickyNavBar({ isSticky: true }));
  }, []);

  return (
    <Grid
      size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
      sx={{ "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
    >
      <BackdropCircularProgress
        open={isSubmitting || isLoadingData}
        testId={testId}
      />
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <SingleColumnLayout
            toolbar={
              <StandardSubmitFormButtonsToolbar
                onResetClicked={onResetClicked}
                isSubmitting={false}
                testId={""}
              />
            }
            testId={`${testId}-toolbar`}
          >
            <StandardPanel
              title="Lookup Field Setup"
              testId={`${testId}-standard-panel`}
              leftSpacing={2}
              rightSpacing={2}
            >
              <RHookFormTextFieldWithLabel
                label="Name"
                id="name"
                testId={`${testId}-field-name`}
                labelPosition={LabelPosition.Left}
                placeholder="Enter Lookup Field Name"
                required
              />
            </StandardPanel>
          </SingleColumnLayout>
        </form>
      </FormProvider>
      <EnhancedSnackbar
        message={snackbarData.message}
        onCloseHandler={onSnackbarClosed}
        severity={snackbarData.severity}
        testId={`${testId}-snackbar`}
        open={snackbarData.open}
      />
    </Grid>
  );
};
